
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { RegisterUserForm } from 'src/app/pages/register-user/interfaces';
import { MovilityType, TripType } from 'src/app/pages/register-user/interfaces/register-user.response';
import { minArrayLengthValidator } from 'src/app/pages/register-user/shared/form/validators/custom-form-validators';

@Injectable({ providedIn: 'root' })

export class RegisterFormService {
    private _fb = inject(FormBuilder);
    private form: FormGroup<RegisterUserForm> = this._fb.group<RegisterUserForm>({
        startDate:            new FormControl<Date | null>(null, Validators.required),
        endDate:              new FormControl<Date | null>(null, Validators.required),
        startTime:            new FormControl<string | null>(null),
        endTime:              new FormControl<string | null>(null),
        startLocationAddress: new FormControl<string>(null, Validators.required),
        startLocationLat:     new FormControl<number>(null, Validators.required),
        startLocationLng:     new FormControl<number>(null, Validators.required),
        endLocation:          new FormControl<boolean>(false, Validators.required),
        movilityType:         new FormControl<MovilityType | null>(null),
        tripType:             new FormControl<TripType>(null, Validators.required),
        interestIdList:       new FormControl<number[]>(null,[Validators.required, minArrayLengthValidator(3)]),
        transportMeans:       new FormControl<string[]>(null,[Validators.required, minArrayLengthValidator(1)]),
        maxDistanceWalking:   new FormControl<number>(null, Validators.required),
        geolocation:          new FormControl<boolean>(false),
        subdomain:            new FormControl<string>('emt', Validators.required),
    });

    private availableTransportMeans = ["andando", "bicimad", "bus-emt", "bus-interurbano", "metro", "metro-ligero", "cercanias"];
    private availableMaxDistance = [200, 500, 1000, 2000, 3000];

    public readonly signalForm = toSignal(
        this.form.valueChanges,
        { initialValue: this.form.getRawValue() }
    );

    public getFormValues(){
        return this.form.value as unknown as RegisterUserForm
    }

    public isValidForm(){
        return this.form.valid;
    }

    public setDateRange(date): void {
        this.form.patchValue({ 
            startDate: date.start_date, 
            endDate: date.end_date 
        });
    }

    public setTripType(group_id) {
        if (!group_id)
            return { 
                is_successful: false, 
                reason: 'Se deben de seleccionar al menos un tipo de viajero'
            }

        this.form.get('tripType').setValue(group_id);
        return { is_successful: true }
    }

    public setInterestIdList(interest_ids) {
        if (interest_ids?.length < 3)
            return { 
                is_successful: false, 
                reason: 'Se deben de seleccionar como mínimo 3 categorías'
            }

        this.form.get('interestIdList').setValue(interest_ids);
        return { is_successful: true }
    }

    public setTransportMeans(transport_methods){
        if (!transport_methods || transport_methods?.length === 0 || 
            !transport_methods.every(transport => this.availableTransportMeans.includes(transport))
        )
            return { 
                is_successful: false, 
                reason: `Las opciones disponibles son ${this.availableTransportMeans.join(',')}`
            }

        this.form.get('transportMeans').setValue(transport_methods);
        return { is_successful: true }
    }

    public setDistancePreferences(max_distance_meters){
        if (!this.availableMaxDistance.includes(max_distance_meters))
            return { 
                is_successful: false, 
                reason: `Las opciones disponibles son ${this.availableMaxDistance.join(',')}`
            }
        
        this.form.get('maxDistanceWalking').setValue(max_distance_meters);
        return { is_successful: true }
    }
    
    public setStartLocation(address){
        const { latitude, longitude, title } = address;
        this.form.get('startLocationAddress').setValue(title);
        this.form.get('startLocationLat').setValue(latitude);
        this.form.get('startLocationLng').setValue(longitude);
        return { is_successful: true }
    }

    public getInvalidControls(): { controlName: string; errors: ValidationErrors }[] {
        const invalidEntries: { controlName: string; errors: ValidationErrors }[] = [];
        Object.keys(this.form.controls).forEach(key => {
            const control = this.form.get(key);
            if (control && control.invalid) {
                invalidEntries.push({
                    controlName: key,
                    errors: control.errors!
                });
            }
        });
        return invalidEntries;
    }

    public resetForm(): void {
        this.form.reset({
            startDate: null,
            endDate: null,
            startTime: null,
            endTime: null,
            startLocationAddress: null,
            startLocationLat: null,
            startLocationLng: null,
            endLocation: false,
            movilityType: null,
            tripType: null,
            interestIdList: [],
            transportMeans: null, //['andando' as any],
            maxDistanceWalking: null, //1000,
            geolocation: false,
            subdomain: 'emt'
        })
    }
}